// External Imports
import React, { useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import { getFormValues, reduxForm, Field } from 'redux-form';
import PropTypes from 'prop-types';

// PPUI Imports
import { Container } from '@paypalcorp/pp-react-grid';
import { Divider } from '@paypalcorp/pp-react';

// Internal Imports
import { BTN_FACTORY_V3_FORM } from '../../clientConstants';
import { validate } from '../../lib/clientValidatorV3';
import AmountSetup from './AmountSetup';
import CoverFeesSwitch from '../switchPanels/CoverFeesSwitch';
import ProgramsV3 from '../programs/ProgramsV3';
import RecurringSwitch from '../switchPanels/RecurringSwitch';
import OrganizationName from '../../shared/OrganizationName';
import Purpose from './components/Purpose';
import { isDonateButton, isHostedFlow } from '../../utilsV3/buttonFactoryUtils';
import WizardNavButtons from '../../shared/navigationButtons/WizardNavButtons';
import FullPageVsPopup from '../FullPageVsPopup';
import StepHeader from './components/StepHeader';
import BannerImage from '../BannerImage/BannerImage';
import DraftErrorWarning from '../../shared/alerts/DraftErrorWarning';
import { FPTI_SPEC } from '../../utilsV3/fptiConstants';
import { trackEventWithData } from '../../utilsV3/analytics';
import LogoExperience from '../logoExperience/LogoExperience';
import RecurringFrequencyOptions from '../switchPanels/RecurringFrequencyOptions';
import _isEmpty from 'lodash/isEmpty';

const renderRadioField = (props) => {
  return <FullPageVsPopup {...props} />;
};

const WizardStep2 = ({
  handleBack,
  handleSubmit,
  application,
  formValues,
  uploadImage,
  updateBusinessProfileLogo,
  updateBusinessImageUrls,
  change,
  dispatch,
  buttonFactoryV3,
  logoClientError,
  bannerClientError,
  handleSaveDraft,
  draftInfo,
  isDraft,
  showDraftError,
  setHandleQuitForm,
  handleQuitDraftSave,
}) => {
  const trackPageLoadImpression = () => {
    if (isDonateButton()) {
      trackEventWithData(FPTI_SPEC.EXPERIENCE_SETUP_PAGE_IM, { step: 2 });
    } else {
      trackEventWithData(FPTI_SPEC.SET_AMOUNT_PAGE_IM, { step: 2 });
    }
  };

  useEffect(() => {
    trackPageLoadImpression();
    setHandleQuitForm?.(() => handleSubmit(handleQuitDraftSave));
  }, []);

  const draftBusy = useSelector(
    (state) => state?.buttonFactoryV3?.draftBusy || false
  );

  const renderLinkFlow = () => {
    return (
      <>
        <AmountSetup
          application={application}
          formValues={formValues}
          change={change}
        />
        {isHostedFlow() && (
          <CoverFeesSwitch
            showBadge={application.showNewFeatureBadge ?? false}
          />
        )}
        <RecurringSwitch />
        {formValues.recurringEnabled === 'yes' && isHostedFlow() ? (
          <RecurringFrequencyOptions />
        ) : null}
        {isHostedFlow() ? <ProgramsV3 /> : null}
      </>
    );
  };

  if (_isEmpty(formValues)) {
    return null;
  }

  return (
    <>
      <Container className="wizard-step-container wizard-step-2-container">
        {showDraftError && <DraftErrorWarning />}
        <StepHeader />
        <form onSubmit={handleSubmit} className="wizard-form">
          {isDonateButton() ? (
            <>
              <OrganizationName />
              {isHostedFlow() ? (
                <>
                  <LogoExperience
                    isChecked={formValues.noLogoImage === 'no'}
                    uploadImage={uploadImage}
                    updateBusinessProfileLogo={updateBusinessProfileLogo}
                    updateBusinessImageUrls={updateBusinessImageUrls}
                    logoUrl={formValues.logoUrl}
                    logoTarget={formValues.logoTarget}
                    businessProfileLogoUrl={formValues.businessProfileLogoUrl}
                    logoClientError={logoClientError}
                    dispatch={dispatch}
                    noLogoImage={formValues.noLogoImage}
                  />
                  <BannerImage
                    isChecked={formValues.noBannerImage === 'no'}
                    uploadImage={uploadImage}
                    bannerImageUrl={formValues.bannerImageUrl}
                    bannerClientError={bannerClientError}
                    dispatch={dispatch}
                    isMobile={buttonFactoryV3.isMobile}
                    showBadge={application.showNewFeatureBadge ?? false}
                  />
                </>
              ) : null}
              <Divider aria-hidden="true" />
              <Purpose />
              {isHostedFlow() ? (
                <>
                  <Divider aria-hidden="true" />
                  <Field
                    value={formValues.useFullPageExperience}
                    name="useFullPageExperience"
                    type="radio"
                    component={renderRadioField}
                    showBadge={application.showBadge ?? false}
                  />
                </>
              ) : null}
            </>
          ) : (
            renderLinkFlow()
          )}
        </form>
      </Container>
      <WizardNavButtons
        handleBack={handleBack}
        handleSubmit={handleSubmit}
        steps={{
          currentStep: buttonFactoryV3?.currentStep,
          totalSteps: buttonFactoryV3?.totalSteps,
        }}
        handleSaveDraft={handleSubmit(handleSaveDraft)}
        draftInfo={draftInfo}
        showDraftSpinner={draftBusy}
        isDraft={isDraft}
        isButton={true}
      />
    </>
  );
};

WizardStep2.propTypes = {
  buttonFactoryV3: PropTypes.object,
  handleBack: PropTypes.func,
  handleSubmit: PropTypes.func,
  application: PropTypes.object,
  formValues: PropTypes.object,
  change: PropTypes.func,
  dispatch: PropTypes.func,
  onSubmit: PropTypes.func,
  logoClientError: PropTypes.string,
  bannerClientError: PropTypes.string,
  updateBusinessProfileLogo: PropTypes.func,
  updateBusinessImageUrls: PropTypes.func,
  rfError: PropTypes.bool,
  rfErrorText: PropTypes.string,
  setRfError: PropTypes.func,
  setRfErrorText: PropTypes.func,
  handleSaveDraft: PropTypes.func,
  draftInfo: PropTypes.string,
  isDraft: PropTypes.bool,
  showDraftError: PropTypes.bool,
};

const mapStateToProps = (state) => {
  const { application = {}, buttonFactoryV3 = {} } = state;

  return {
    application,
    buttonFactoryV3,
    formValues: getFormValues(BTN_FACTORY_V3_FORM)(state),
    initialValues: buttonFactoryV3.formattedButton,
  };
};

const mapDispatchToProps = (dispatch) => {
  return { dispatch };
};

let WizardStep2Form = reduxForm({
  form: BTN_FACTORY_V3_FORM,
  keepDirtyOnReinitialize: true,
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  validate,
})(WizardStep2);

WizardStep2Form = connect(mapStateToProps, mapDispatchToProps)(WizardStep2Form);

export default WizardStep2Form;
